import React, {
  useEffect,
  useState,
  useCallback,
  createContext,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { loog, getPath, doEncryptLocal } from '@express-labs/raven-tools';
import { useQuery } from '@apollo/client';
import { getCustomerLoyaltyRewards, getOrderSummary, getCreditCardDiscount } from '../../queries';
import getFromStorage from '../../helpers/storage/storage';
import { creditCardDiscount } from '../../constants';

export const HeaderDynamicContext = createContext();

function HeaderDynamicData(props) {
  const {
    data: creditCardData,
  } = useQuery(getCreditCardDiscount, {
    fetchPolicy: 'cache-first',
  });

  const discount = creditCardData?.getCreditCardDiscount?.creditCardDiscount
        ?? creditCardDiscount;

  const {
    loading,
    data: orderSummaryData,
  } = useQuery(getOrderSummary, {
    variables: {
      shouldRecalculateTax: false,
    },
  });
  const { children } = props;
  const [data, setData] = useState({});
  const [customer, setCustomer] = useState({});
  const [heartBeatData, setHeartBeatData] = useState({});
  const [customerStore, setCustomerStore] = useState({});
  const [loyaltyWarmStateData, setLoyaltyWarmStateData] = useState({});
  const [loyaltyId, setLoyaltyId] = useState('');
  const [loyaltyRewards, setLoyaltyRewards] = useState({});
  const [orderSummary, setOrderSummary] = useState();
  const [error, setError] = useState('');

  const {emailAddress} = customer;
  const isLoyaltyMigrationEnabled = window.FLAGS?.LoyaltyMigration;
  const userId = isLoyaltyMigrationEnabled ? emailAddress : loyaltyId;

  const memberData = useQuery(
    getCustomerLoyaltyRewards,
    {
      variables: { memberId: userId },
      skip: !userId,
    },
  );

  // todo remove the wrappers around setState
  const getDynamicData = useCallback((dynamicData) => {
    setData(dynamicData);
  }, [],);

  const setOrderSummaryData = useCallback((bagSummary) => {
    setOrderSummary(bagSummary);
  }, [],);

   /* istanbul ignore next */
  useEffect(() => {
    setOrderSummary(orderSummaryData);
     /* istanbul ignore next */
    if (!loading && orderSummaryData) {
      const customerStoreInfo = orderSummaryData?.orderSummary?.customerStoreInfo?.orderStore ?? {};
      setCustomerStore(customerStoreInfo);
    }
  }, [loading, orderSummaryData]);

  // Heartbeat data
  useEffect(() => {
    window.Heartbeat.beating().always((dynamicData) => {
      getDynamicData(dynamicData);
      // Set it to continue updating on subsequent beats
      window.Heartbeat.on('beat', 'updateBagCount', getDynamicData);
    });
  }, [getDynamicData]);

  // Customer data
  useEffect(() => {
    function decryptCustomer(hbeatData) {
      const sessionData = window.hasStorage !== false ? sessionStorage.getItem('hbSession') : null;

      if (sessionData) {
        try {
          const [ct, iv, salt] = sessionData.split('|');

          const userData = doEncryptLocal.doDecrypt(
            `${hbeatData.anon ? 'A' : 'L'}${hbeatData.uid}`,
            `{"iv":"${iv}","v":1,"iter":10000,"ks":128,"ts":64,"mode":"ccm","adata":"","cipher":"aes","salt":"${salt}","ct":"${ct}"}`,
          );

          const json = JSON.parse(userData);

          const { customerActedOnENCCAsPreferredModal } = json;
          const customerInfo = getPath(json, 'customer.customerInfo', {});
          customerInfo.customerActedOnENCCAsPreferredModal = !!customerActedOnENCCAsPreferredModal;
          setCustomer(customerInfo);
          setHeartBeatData(json);
        } catch (e) {
          if (window.isDebugMode) {
            loog(`Error decrypting session data: ${e}`, 'info', e);
          }
          sessionStorage.removeItem('hbSession');
          setError(e);
        }
      }
    }
    window.Heartbeat.beating().always(decryptCustomer);
  }, [data, data.anon, data.uid]);

  // Update Customer Data through Eva
  useEffect(() => {
    function getCustomer(e) {
      setCustomer(getPath(e, 'detail.customer.customerInfo', {}));
      const customerStoreInfo = e?.detail?.customerStore ?? {};
      setCustomerStore(customerStoreInfo);
    }
    window.addEventListener('updateHeaderCustomerData', getCustomer);
    return () => {
      window.removeEventListener('updateHeaderCustomerData', getCustomer);
    };
  }, [customerStore]);

  useEffect(() => {
    const warmStateData = getFromStorage('loyaltyWarmStateData', {});
    const hasLoyaltyData = Object.entries(warmStateData).length > 0;
    const memberId = hasLoyaltyData ? warmStateData?.loyaltyNumber : customer?.loyaltyNumber;

    setLoyaltyWarmStateData(warmStateData);
    setLoyaltyId(memberId);

    const customerRewards = memberData?.data?.getCustomerLoyaltyRewards || {};
    setLoyaltyRewards(customerRewards);
  }, [customer?.loyaltyNumber, memberData]);

  const context = useMemo(() => ({
    data,
    heartBeatData,
    customer,
    customerStore,
    error,
    loyaltyWarmStateData,
    loyaltyRewards,
    orderSummary,
    orderSummaryLoading: loading,
    discount,
    setOrderSummaryData,
    setCustomerStore,
  // eslint-disable-next-line max-len
  }), [data, heartBeatData, customer, customerStore, error, loyaltyWarmStateData, loyaltyRewards, orderSummary, loading, discount, setOrderSummaryData]);

  return (
    <HeaderDynamicContext.Provider value={context}>
      {children}
    </HeaderDynamicContext.Provider>
  );
}

HeaderDynamicData.meta = {
  VERSION: '1.0.0',
};

HeaderDynamicData.propTypes = {
  children: PropTypes.node.isRequired,
};

export default HeaderDynamicData;
